import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAppraisal, type AppraisalDetail, type FileContactRole, type FileContactRoleResponse } from '../lib/appraisal';
import { listDocumentsForJob } from '../lib/documents';
import { listNotepadEntries } from '../lib/notepad';
import { summarizeJobSync, type JobSyncSummary } from '../lib/jobStatus';
import { getMapsLink } from '../lib/navigate';
import { getRouteEstimate, recalculateRouteEstimate, type InspectionRouteEstimate } from '../lib/routeEstimate';
import {
  createFieldStatusEvent,
  getLatestFieldStatus,
  startDriveSession,
  type FieldStatusValue,
} from '../lib/fieldEvents';
import { getItemsByJob } from '../lib/syncQueue';
import {
  formatDistanceMeters,
  formatDurationFromMinutes,
  formatDurationFromSeconds,
  formatEpochMs,
  formatShortDateTime,
  formatTime,
  routeStatusClass,
  routeStatusLabel,
} from '../lib/fieldRouteFormat';

function addressOf(a: AppraisalDetail): string {
  if (a.property_address) return a.property_address;
  const parts = [a.address_street, a.address_city, a.address_state, a.address_zip].filter(Boolean);
  return parts.length ? parts.join(', ') : '';
}

const CONTACT_ROLES: FileContactRole[] = ['policyholder', 'carrier', 'opposingAppraiser', 'umpire'];
type ContactCardVariant = 'teal' | 'gray' | 'amber';

function contactVariant(role: FileContactRole, status: 'contactable' | 'offLimits') {
  if (role === 'umpire') return 'amber' as const;
  if (status === 'offLimits' || role === 'opposingAppraiser') return 'gray' as const;
  return 'teal' as const;
}

function contactInitials(name: string | null | undefined, fallback: string) {
  const source = name?.trim() || fallback;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
}

function contactVariantClasses(variant: ContactCardVariant) {
  if (variant === 'amber') {
    return {
      avatar: 'border-bracket-amber/30 bg-bracket-amber/15 text-bracket-amber',
      pill: 'border-bracket-amber/30 bg-bracket-amber/10 text-bracket-amber',
    };
  }
  if (variant === 'teal') {
    return {
      avatar: 'border-eva-teal/30 bg-eva-teal/10 text-eva-teal',
      pill: 'border-eva-teal/30 bg-eva-teal/10 text-eva-teal',
    };
  }
  return {
    avatar: 'border-gray-200 bg-gray-100 text-gray-700',
    pill: 'border-gray-200 bg-gray-50 text-gray-600',
  };
}

export function JobDetail() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AppraisalDetail | null>(null);
  const [sync, setSync] = useState<JobSyncSummary | null>(null);
  const [routeEstimate, setRouteEstimate] = useState<InspectionRouteEstimate | null>(null);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeErr, setRouteErr] = useState<string | null>(null);
  const [photoCount, setPhotoCount] = useState(0);
  const [flaggedCount, setFlaggedCount] = useState(0);
  const [documentCount, setDocumentCount] = useState<number | null>(null);
  const [stormReportAvailable, setStormReportAvailable] = useState(false);
  const [notepadCount, setNotepadCount] = useState<number | null>(null);
  const [jobStatus, setLocalJobStatus] = useState<string | null>(null);
  const [fieldStatusSaving, setFieldStatusSaving] = useState<FieldStatusValue | null>(null);
  const [fieldEventErr, setFieldEventErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [showReinspect, setShowReinspect] = useState(false);
  const [contactsOpen, setContactsOpen] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const [a, s, items] = await Promise.all([
          getAppraisal(jobId),
          summarizeJobSync(jobId),
          getItemsByJob(jobId),
        ]);
        if (cancelled) return;
        setData(a);
        setLocalJobStatus(a.status);
        setSync(s);
        setPhotoCount(items.length);
        setFlaggedCount(
          items.filter((i) => {
            const di = i.metadata.device_info as { flagged?: boolean } | undefined;
            return di?.flagged === true;
          }).length,
        );
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const latest = await getLatestFieldStatus(jobId);
        if (!cancelled && latest.status) setLocalJobStatus(latest.status);
      } catch {
        // Field status is progressive enhancement; the appraisal can still load.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const docs = await listDocumentsForJob(jobId);
        if (cancelled) return;
        setDocumentCount(docs.length);
        setStormReportAvailable(
          docs.some((d) => (d.type ?? '').toLowerCase().includes('storm')),
        );
      } catch {
        if (!cancelled) {
          setDocumentCount(null);
          setStormReportAvailable(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const entries = await listNotepadEntries(jobId);
        if (!cancelled) setNotepadCount(entries.length);
      } catch {
        if (!cancelled) setNotepadCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    if (!jobId || !data) return;
    const scheduled = data.inspections?.slice().sort((a, b) => {
      const da = formatEpochMs(a.date);
      const db = formatEpochMs(b.date);
      return da - db;
    })[0] ?? null;
    if (!scheduled) return;
    let cancelled = false;
    setRouteLoading(true);
    setRouteErr(null);
    (async () => {
      try {
        const estimate = await getRouteEstimate(jobId, scheduled.id);
        if (!cancelled) setRouteEstimate(estimate);
      } catch (e) {
        if (!cancelled) setRouteErr((e as Error).message);
      } finally {
        if (!cancelled) setRouteLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data, jobId]);

  if (!jobId) return <div className="p-4 text-sm text-red-600">Missing job ID.</div>;
  if (err) {
    return (
      <div className="mx-auto max-w-2xl p-4">
        <Link to="/" className="text-sm text-gray-600 underline">‹ Jobs</Link>
        <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          Error loading job: {err}
        </div>
      </div>
    );
  }
  if (!data) return <div className="p-4 text-sm text-gray-500">Loading…</div>;

  const address = addressOf(data);
  const adjuster =
    data.contacts?.find((c) => (c.role ?? '').toLowerCase() === 'adjuster')?.name ?? null;
  const scheduledInspection =
    data.inspections?.slice().sort((a, b) => {
      const da = formatEpochMs(a.date);
      const db = formatEpochMs(b.date);
      return da - db;
    })[0] ?? null;
  const effectiveStatus = jobStatus ?? data.status;
  const isCompleted = ['completed', 'closed', 'archived'].includes(
    (effectiveStatus ?? '').toLowerCase(),
  );
  const hasLocalPhotos = photoCount > 0;
  const displayTimeZone = null;

  let ctaLabel: string;
  let ctaDisabled = false;
  let ctaAction: () => void;
  if (isCompleted) {
    ctaLabel = 'Reinspect';
    ctaAction = () => setShowReinspect(true);
  } else if (hasLocalPhotos) {
    ctaLabel = sync?.status === 'synced' ? 'Continue Inspection' : 'Resume Inspection';
    ctaAction = () => navigate(`/job/${jobId}/camera?mode=new`);
  } else {
    ctaLabel = 'Start Inspection';
    ctaAction = () => navigate(`/job/${jobId}/camera?mode=new`);
  }

  async function handleNavigate() {
    if (!address) return;
    try {
      await startDriveSession({
        jobId: jobId!,
        inspectionId: scheduledInspection?.id ?? null,
        routeEstimateId: routeEstimate?.id ?? null,
        direction: 'outbound',
        metadata: { navigation_provider: 'device_default_maps' },
      });
    } catch {
      // Navigation should still work if event capture is temporarily unavailable.
    }
    window.location.href = getMapsLink(address);
  }

  async function setJobStatus(jobId: string, status: FieldStatusValue) {
    setFieldStatusSaving(status);
    setFieldEventErr(null);
    try {
      await createFieldStatusEvent({
        jobId,
        status,
        inspectionId: scheduledInspection?.id ?? null,
        metadata: { outbound_notifications: false },
      });
      setLocalJobStatus(status);
      if (status === 'inspecting') ctaAction();
    } catch (e) {
      setFieldEventErr((e as Error).message);
    } finally {
      setFieldStatusSaving(null);
    }
  }

  async function handleRecalculateRoute() {
    if (!scheduledInspection) return;
    setRouteLoading(true);
    setRouteErr(null);
    try {
      const estimate = await recalculateRouteEstimate(jobId!, scheduledInspection.id);
      setRouteEstimate(estimate);
    } catch (e) {
      setRouteErr((e as Error).message);
    } finally {
      setRouteLoading(false);
    }
  }

  return (
    <div className="mx-auto max-w-2xl p-4 pb-28">
      <Link to="/" className="text-sm text-gray-600 underline">
        ‹ Jobs
      </Link>

      {/* Header */}
      <div className="mt-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate text-xl font-bold">
            {data.policyholder_name || '(no name)'}
          </h1>
          {data.claim_number && (
            <div className="text-xs text-gray-500">Claim {data.claim_number}</div>
          )}
        </div>
        {effectiveStatus && (
          <span className="shrink-0 rounded-full bg-gray-100 px-2 py-1 text-[10px] font-bold uppercase text-gray-600">
            {effectiveStatus}
          </span>
        )}
      </div>

      {/* Address card */}
      <div className="mt-4 rounded-lg border border-gray-200 bg-white p-3">
        <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
          Address
        </div>
        <div className="mt-1 text-sm">{address || '(no address)'}</div>
        <button
          onClick={handleNavigate}
          disabled={!address}
          className="mt-2 rounded-lg bg-eva-teal px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
        >
          Navigate
        </button>
      </div>

      {/* Reference vault */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <VaultTile
          to={`/job/${jobId}/documents`}
          label="Documents"
          indicator={formatCountIndicator(documentCount, 'file', 'files')}
        />
        <VaultTile
          to={`/job/${jobId}/photos`}
          label="Photos"
          indicator={formatCountIndicator(photoCount, 'photo', 'photos')}
          detail={flaggedCount > 0 ? `${flaggedCount} flagged` : undefined}
        />
        <VaultTile
          to={`/job/${jobId}/notepad`}
          label="Notepad"
          indicator={formatCountIndicator(notepadCount, 'note', 'notes')}
        />
        {stormReportAvailable && (
          <VaultTile
            to={`/job/${jobId}/documents`}
            label="Storm report"
            indicator="Available"
          />
        )}
      </div>

      {/* Info grid */}
      <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
        <InfoTile label="Carrier" value={data.carrier_name} required />
        <InfoTile label="Adjuster" value={adjuster} />
        <InfoTile label="Claim #" value={data.claim_number} required />
        <InfoTile
          label="Scheduled"
          value={
            scheduledInspection?.date
              ? formatShortDateTime(scheduledInspection.date, displayTimeZone)
              : null
          }
        />
        <InfoTile label="Type of Loss" value={data.loss_type} />
        <InfoTile
          label="Date of Loss"
          value={data.loss_date ? new Date(data.loss_date).toLocaleDateString() : null}
          required
        />
      </div>

      <RouteEstimateCard
        address={address}
        estimate={routeEstimate}
        inspectionDurationMinutes={scheduledInspection?.duration_minutes ?? null}
        inspectionDate={scheduledInspection?.date ?? null}
        displayTimeZone={displayTimeZone}
        loading={routeLoading}
        error={routeErr}
        onRecalculate={handleRecalculateRoute}
      />

      {/* Appointment note */}
      {scheduledInspection?.notes && (
        <div className="mt-3 rounded-lg border border-bracket-amber/40 bg-bracket-amber/10 p-3 text-sm">
          <div className="text-[10px] font-bold uppercase tracking-wide text-bracket-amber">
            Appointment note
          </div>
          <div className="mt-0.5">{scheduledInspection.notes}</div>
        </div>
      )}

      <FieldActionDock
        jobId={jobId}
        ctaLabel={ctaLabel}
        onStatusChange={setJobStatus}
        onPrimary={ctaAction}
        onContacts={() => setContactsOpen(true)}
        primaryDisabled={ctaDisabled}
        primaryStatus={isCompleted ? null : 'inspecting'}
        savingStatus={fieldStatusSaving}
      />

      {fieldEventErr && (
        <div className="fixed inset-x-3 bottom-20 z-40 mx-auto max-w-2xl rounded-xl border border-bracket-amber/40 bg-bracket-amber/10 p-3 text-xs text-bracket-amber shadow-lg">
          Field status was not saved: {fieldEventErr}
        </div>
      )}

      {contactsOpen && (
        <ContactsBottomSheet
          file={data}
          onClose={() => setContactsOpen(false)}
        />
      )}

      {/* Reinspection modal */}
      {showReinspect && (
        <ReinspectionModal
          jobId={jobId}
          onClose={() => setShowReinspect(false)}
        />
      )}
    </div>
  );
}

function RouteEstimateCard({
  address,
  estimate,
  inspectionDurationMinutes,
  inspectionDate,
  displayTimeZone,
  loading,
  error,
  onRecalculate,
}: {
  address: string;
  estimate: InspectionRouteEstimate | null;
  inspectionDurationMinutes: number | null;
  inspectionDate: string | null;
  displayTimeZone: string | null;
  loading: boolean;
  error: string | null;
  onRecalculate: () => void;
}) {
  const hasRouteData = Boolean(
    estimate?.travel_duration_seconds || estimate?.distance_meters || estimate?.leave_by_time,
  );
  const inspectLabel = formatDurationFromMinutes(inspectionDurationMinutes);

  if (!hasRouteData) {
    return (
      <div className="mt-4 rounded-xl border border-eva-teal/20 bg-white p-3 text-sm shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wide text-eva-teal">
              Route to inspection
            </div>
            <div className="mt-1 truncate text-gray-700">
              {loading ? 'Checking route…' : `Route not calculated · ${inspectLabel} inspect`}
            </div>
          </div>
          <button
            onClick={onRecalculate}
            disabled={loading || !inspectionDate}
            className="shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-700 disabled:opacity-50"
          >
            {loading ? 'Updating…' : 'Update'}
          </button>
        </div>
        {error && (
          <div className="mt-2 rounded-lg border border-bracket-amber/30 bg-bracket-amber/10 p-2 text-xs text-bracket-amber">
            Route unavailable: {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-2xl border border-eva-teal/20 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wide text-eva-teal">
            Route to inspection
          </div>
          <div className="mt-1 text-lg font-bold text-gray-950">
            {estimate?.leave_by_time ? formatTime(estimate.leave_by_time, displayTimeZone) : 'Route calculated'}
          </div>
          <div className="text-xs text-gray-500">
            Leave-by {estimate?.leave_by_time ? 'time' : 'pending'}
          </div>
        </div>
        <span className={`rounded-full border px-2 py-1 text-[10px] font-bold uppercase ${routeStatusClass(estimate?.status)}`}>
          {loading ? 'Loading' : routeStatusLabel(estimate?.status)}
        </span>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
        <RouteMetric label="Drive" value={formatDurationFromSeconds(estimate?.travel_duration_seconds)} />
        <RouteMetric label="Miles" value={formatDistanceMeters(estimate?.distance_meters)} />
        <RouteMetric label="Inspect" value={inspectLabel} />
      </div>

      <div className="mt-3 rounded-xl bg-gray-50 p-3 text-xs text-gray-600">
        <div>
          <span className="font-semibold text-gray-900">Appointment:</span>{' '}
          {formatShortDateTime(inspectionDate, displayTimeZone)}
        </div>
        <div className="mt-1 truncate">
          <span className="font-semibold text-gray-900">Destination:</span>{' '}
          {estimate?.destination_address || address || 'No address'}
        </div>
        {estimate?.calculated_at && (
          <div className="mt-1">
            Updated {formatShortDateTime(estimate.calculated_at, displayTimeZone)}
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-bracket-amber/30 bg-bracket-amber/10 p-2 text-xs text-bracket-amber">
          Route unavailable: {error}
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <button
          onClick={onRecalculate}
          disabled={loading || !inspectionDate}
          className="rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm font-bold text-gray-700 disabled:opacity-50"
        >
          {loading ? 'Updating…' : 'Update Route'}
        </button>
      </div>
    </div>
  );
}

function ContactsBottomSheet({ file, onClose }: { file: AppraisalDetail; onClose: () => void }) {
  const contacts = file.file_contacts?.roles;

  return (
    <div className="fixed inset-0 z-40 bg-black/40" role="dialog" aria-modal="true" aria-label="File contacts">
      <button
        type="button"
        className="absolute inset-0 h-full w-full"
        aria-label="Close contacts"
        onClick={onClose}
      />
      <div className="absolute inset-x-0 bottom-0 mx-auto flex max-h-[85vh] max-w-2xl flex-col rounded-t-2xl bg-gray-50 shadow-2xl">
        <div className="shrink-0 border-b border-gray-200 bg-white p-4">
          <div className="mx-auto mb-3 h-1.5 w-12 rounded-full bg-gray-300" />
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-lg font-bold text-gray-950">Contacts</div>
              <div className="mt-0.5 text-sm text-gray-500">
                Representing: {file.file_contacts?.representing_party === 'carrier' ? 'Carrier' : 'Policyholder'}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-gray-200 bg-white px-3 py-1.5 text-sm font-bold text-gray-700"
            >
              Close
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-3 overflow-y-auto p-4 pb-6">
          {contacts ? (
            CONTACT_ROLES.map((role) => {
              const contact = contacts[role];
              if (!contact) return null;
              return (
                <FileContactCard
                  key={role}
                  contact={contact}
                  variant={contactVariant(role, contact.status)}
                />
              );
            })
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-500">
              Contacts are not available for this file yet.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FileContactCard({
  contact,
  variant,
}: {
  contact: FileContactRoleResponse;
  variant: ContactCardVariant;
}) {
  const classes = contactVariantClasses(variant);
  const name = contact.contact.name?.trim();
  const firm = contact.contact.firm?.trim();
  const phone = contact.contact.phone?.trim();
  const email = contact.contact.email?.trim();
  const isOffLimits = contact.status === 'offLimits';
  const hasDetails = Boolean(name || firm || phone || email);
  const canCallOrText = !isOffLimits && Boolean(phone);

  function handlePhoneAction(kind: 'call' | 'text') {
    if (isOffLimits || !phone) return;
    window.location.href = `${kind === 'call' ? 'tel' : 'sms'}:${phone}`;
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-sm font-bold ${classes.avatar}`}>
          {hasDetails ? contactInitials(name, contact.label) : '•'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="truncate text-sm font-bold text-gray-950">{name || 'Unassigned'}</div>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${classes.pill}`}>
              {contact.label}
            </span>
          </div>
          {firm && <div className="mt-1 truncate text-xs text-gray-500">{firm}</div>}
          {phone && <div className="mt-2 text-sm text-gray-700">{phone}</div>}
          {email && <div className="truncate text-sm text-gray-700">{email}</div>}
          {!hasDetails && <div className="mt-2 text-sm text-gray-500">No contact assigned</div>}
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {isOffLimits && (
          <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
            Locked · {contact.reason || 'Contact through opposing appraiser'}
          </div>
        )}
        {contact.caution && (
          <div className="rounded-lg border border-bracket-amber/30 bg-bracket-amber/10 px-3 py-2 text-xs text-bracket-amber">
            {contact.caution}
          </div>
        )}
        {!isOffLimits && hasDetails && (
          <div className="flex gap-2">
            {canCallOrText ? (
              <>
                <button
                  type="button"
                  onClick={() => handlePhoneAction('call')}
                  className="flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-bold text-gray-800"
                >
                  Call
                </button>
                <button
                  type="button"
                  onClick={() => handlePhoneAction('text')}
                  className="flex-1 rounded-xl bg-eva-teal px-3 py-2 text-sm font-bold text-white"
                >
                  Text
                </button>
              </>
            ) : (
              <div className="text-xs text-gray-500">No phone saved</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RouteMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl bg-gray-50 p-2">
      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{label}</div>
      <div className="mt-0.5 font-bold text-gray-950">{value}</div>
    </div>
  );
}

function FieldActionDock({
  jobId,
  ctaLabel,
  onStatusChange,
  onPrimary,
  onContacts,
  primaryDisabled,
  primaryStatus,
  savingStatus,
}: {
  jobId: string;
  ctaLabel: string;
  onStatusChange: (jobId: string, status: FieldStatusValue) => void;
  onPrimary: () => void;
  onContacts: () => void;
  primaryDisabled: boolean;
  primaryStatus: 'inspecting' | null;
  savingStatus: FieldStatusValue | null;
}) {
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 border-t border-gray-200 bg-white/95 p-3 shadow-[0_-8px_30px_rgba(15,23,42,0.12)] backdrop-blur">
      <div className="mx-auto grid max-w-2xl grid-cols-4 gap-2">
        <button
          onClick={() => onStatusChange(jobId, 'on_way')}
          disabled={savingStatus !== null}
          title="Save internal on-way status"
          className="rounded-xl border border-gray-200 bg-white px-2 py-2 text-xs font-bold text-gray-700 disabled:opacity-40"
        >
          {savingStatus === 'on_way' ? 'Saving' : 'On Way'}
        </button>
        <button
          onClick={() => onStatusChange(jobId, 'arrived')}
          disabled={savingStatus !== null}
          title="Save internal arrived status"
          className="rounded-xl border border-gray-200 bg-white px-2 py-2 text-xs font-bold text-gray-700 disabled:opacity-40"
        >
          {savingStatus === 'arrived' ? 'Saving' : 'Arrived'}
        </button>
        <button
          onClick={onContacts}
          className="rounded-xl border border-eva-teal/30 bg-white px-2 py-2 text-xs font-bold text-eva-teal"
        >
          Contacts
        </button>
        <button
          onClick={() => {
            if (primaryStatus) onStatusChange(jobId, primaryStatus);
            else onPrimary();
          }}
          disabled={primaryDisabled}
          className="rounded-xl bg-eva-teal px-2 py-2 text-xs font-bold text-white disabled:opacity-50"
        >
          {savingStatus === 'inspecting' ? 'Saving' : ctaLabel.replace(' Inspection', '')}
        </button>
      </div>
    </div>
  );
}

function formatCountIndicator(
  count: number | null,
  singular: string,
  plural: string,
): string | undefined {
  if (count == null) return undefined;
  if (count === 0) return 'Empty';
  return `${count} ${count === 1 ? singular : plural}`;
}

function VaultTile({
  to,
  label,
  indicator,
  detail,
}: {
  to: string;
  label: string;
  indicator?: string;
  detail?: string;
}) {
  return (
    <Link
      to={to}
      className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-3 text-sm"
    >
      <div className="min-w-0">
        <div className="font-semibold">{label}</div>
        {(indicator || detail) && (
          <div className="mt-0.5 truncate text-xs text-gray-500">
            {indicator}
            {detail && <span className="ml-1 text-red-500">· {detail}</span>}
          </div>
        )}
      </div>
      <span className="text-gray-400">›</span>
    </Link>
  );
}

function InfoTile({
  label,
  value,
  required = false,
}: {
  label: string;
  value: string | null | undefined;
  required?: boolean;
}) {
  const missing = !value;
  return (
    <div className={`rounded-lg border bg-white p-3 ${missing ? 'border-dashed border-gray-200' : 'border-gray-200'}`}>
      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className={`mt-0.5 font-semibold ${missing ? 'text-gray-400' : 'text-gray-900'}`}>
        {value || (required ? 'Required missing' : '—')}
      </div>
    </div>
  );
}

function ReinspectionModal({
  jobId,
  onClose,
}: {
  jobId: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [choice, setChoice] = useState<'new_set' | 'add_to_existing' | null>(null);
  function confirm() {
    if (!choice) return;
    navigate(`/job/${jobId}/camera?mode=reinspection&rtype=${choice}`);
  }
  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-black/60 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-t-2xl bg-white p-4 sm:rounded-2xl"
      >
        <h2 className="text-lg font-bold">Reinspect this job</h2>
        <p className="mt-1 text-sm text-gray-600">
          This job is already complete. How should the new session number its photos?
        </p>
        <div className="mt-4 space-y-2">
          <button
            onClick={() => setChoice('new_set')}
            className={`w-full rounded-lg border p-3 text-left ${
              choice === 'new_set'
                ? 'border-eva-teal bg-eva-teal/10'
                : 'border-gray-300 bg-white'
            }`}
          >
            <div className="font-semibold">New Photo Set</div>
            <div className="text-xs text-gray-600">
              Fresh session; sequence starts at #1.
            </div>
          </button>
          <button
            onClick={() => setChoice('add_to_existing')}
            className={`w-full rounded-lg border p-3 text-left ${
              choice === 'add_to_existing'
                ? 'border-eva-teal bg-eva-teal/10'
                : 'border-gray-300 bg-white'
            }`}
          >
            <div className="font-semibold">Add to Existing</div>
            <div className="text-xs text-gray-600">
              Continues from the last sequence + 1.
            </div>
          </button>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg border border-gray-300 bg-white py-2 text-sm font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={!choice}
            className="flex-1 rounded-lg bg-eva-teal py-2 text-sm font-semibold text-white disabled:opacity-40"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}
